// src/context/UserProfileContext.tsx
import React, { createContext, useContext, useMemo, useState } from 'react';

export type UserProfile = {
  cognitoAttributes: any;
  lastName: string;
  firstName: string;
  lastNameKana: string;
  firstNameKana: string;
  department: string;
  team: string;
  preferredTeamId: string;
  preferredTeamLabel: string;
  preferredTeamUpdatedAt: string;
  authority: number | null;
};

type Ctx = {
  profile: UserProfile;
  setProfile: (update: Partial<UserProfile>) => void;
  resetProfile: () => void;
  loaded: boolean;
  setLoaded: (v: boolean) => void;
};

const initialProfile: UserProfile = {
  lastName: '',
  firstName: '',
  lastNameKana: '',
  firstNameKana: '',
  department: '',
  team: '',
  preferredTeamId: '',
  preferredTeamLabel: '',
  preferredTeamUpdatedAt: '',
  cognitoAttributes: undefined,
  authority: null,
};

const UserProfileContext = createContext<Ctx>({
  profile: initialProfile,
  setProfile: () => {},
  resetProfile: () => {},
  loaded: false,
  setLoaded: () => {},
});

export const UserProfileProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [profile, setProfileState] = useState<UserProfile>(initialProfile);
  const [loaded, setLoaded] = useState<boolean>(false);

  const setProfile = (update: Partial<UserProfile>) => {
    setProfileState((prev) => ({ ...prev, ...update }));
    // プロフィール情報が一度でも設定されたら、判定可能状態にする
    setLoaded(true);
  };

  const resetProfile = () => {
    setProfileState(initialProfile);
    setLoaded(false);
  };

  const value = useMemo(() => ({ profile, setProfile, resetProfile, loaded, setLoaded }), [profile, loaded]);

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
};

export const useUserProfile = () => useContext(UserProfileContext);

// 権限レベル定義（数値が大きいほど強い権限。値は USER_AUTH_MANAGE 側の auth と対応）
// 1: 一般管理者 / 2: アプリ管理者 / 9: 開発者（3〜8は将来の拡張用に予約）
export const AUTHORITY_LEVELS = {
  GENERAL_ADMIN: 1,
  APP_ADMIN: 2,
  DEVELOPER: 9,
} as const;

// 管理者権限判定（authority >= 1 を管理者とみなす）。マスタ管理系画面で共通利用する。
export const isAdminAuthority = (authority: number | null | undefined): boolean =>
  (authority ?? 0) >= AUTHORITY_LEVELS.GENERAL_ADMIN;

export const useIsAdmin = (): boolean => {
  const { profile } = useUserProfile();
  return isAdminAuthority(profile?.authority);
};

// アプリ管理者権限判定（authority >= 2）
export const isAppAdminAuthority = (authority: number | null | undefined): boolean =>
  (authority ?? 0) >= AUTHORITY_LEVELS.APP_ADMIN;

export const useIsAppAdmin = (): boolean => {
  const { profile } = useUserProfile();
  return isAppAdminAuthority(profile?.authority);
};

// 開発者権限判定（authority === 9）
export const isDeveloperAuthority = (authority: number | null | undefined): boolean =>
  (authority ?? 0) === AUTHORITY_LEVELS.DEVELOPER;

export const useIsDeveloper = (): boolean => {
  const { profile } = useUserProfile();
  return isDeveloperAuthority(profile?.authority);
};
